// src/core/context/theme-context.tsx
import React, { createContext, useContext, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import getTheme from "@core/theme/theme";

export type ThemeMode = "light" | "dark" | "blue" | "green";
export const themeModes: ThemeMode[] = ["light", "dark"];

export type ThemeContextType = {
  toggleTheme: (newTheme?: ThemeMode) => void;
  mode: ThemeMode;
  themeModes: ThemeMode[];
};

const ThemeContext = createContext<ThemeContextType>({} as ThemeContextType);

const ThemeProvider: React.FC<{
  children: React.ReactNode;
  initialMode: ThemeMode;
}> = ({ children, initialMode }) => {
  const [mode, setMode] = React.useState<ThemeMode>(initialMode);
  const theme = useMemo(() => getTheme(mode), [mode]);

  const toggleTheme = (newTheme?: ThemeMode) => {
    if (newTheme) {
      setMode(newTheme);
      document.cookie = `theme=${newTheme}; path=/; max-age=31536000; SameSite=Lax`;
    } else {
      const currentIndex = themeModes.indexOf(mode);
      const nextMode = themeModes[(currentIndex + 1) % themeModes.length];
      setMode(nextMode);
      document.cookie = `theme=${nextMode}; path=/; max-age=31536000; SameSite=Lax`;
    }
  };

  return (
    <ThemeContext.Provider value={{ toggleTheme, mode, themeModes }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export { ThemeContext };
export default ThemeProvider;
